<script>
    export let response;

    import StatusBtn from "./StatusBtn.svelte";
    import NotificationBtn from "./NotificationBtn.svelte";
    import DownloadCardBtn from "./DownloadCardBtn.svelte";
    import DownloadPieceBtn from "./DownloadPieceBtn.svelte";
    import EmployeeUrl from "./EmployeeUrl.svelte";
    import ReloadPieces from "./ReloadPieces.svelte";
    import BulkNotificationBtn from "./BulkNotificationBtn.svelte";
    import Icon from "./Icon.svelte";
    import { approve, reject, sendNotification } from "../http.js";

    const statusList = {
        'NOSELECTED': 'Nie przesłano',
        'SELECTED': 'Czeka na akceptację',
        'APPROVED': 'Zaakceptowano',
        'REJECTED': 'Odrzucono'
    };

    const statusFilter = (status) => statusList[status];

    const statusClass = (status) => `status-${status.toLowerCase()}`;

    const keyOf = (work) => {
        const id = work.employee_id;

        return `${id.employee_email}-${id.month}-${id.year}`;
    };

    const MENU_HEIGHT = 180;

    let period = null;
    let menuPos = { top: 0, right: 0 };

    const periodOf = (work) => `${work.employee_id.month}.${work.employee_id.year}`;

    $: periods = [...new Set($response.map(periodOf))];
    $: if (period === null && periods.length > 0) period = periods[0];
    $: visible = period === null ? $response : $response.filter(work => periodOf(work) === period);

    let selected = [];
    let expanded = [];
    let openPieces = [];
    let openMenu = null;
    let busy = false;

    const toggle = (list, key) => {
        return list.includes(key) ? list.filter(k => k !== key) : [...list, key];
    };

    const selectable = (works) => works.filter(work => work.status !== 'NOSELECTED');

    const openRowMenu = (event, key) => {
        if (openMenu === key) {
            openMenu = null;
            return;
        }

        const rect = event.currentTarget.getBoundingClientRect();
        const below = window.innerHeight - rect.bottom;
        const flip = below < MENU_HEIGHT;

        menuPos = {
            top: flip ? rect.top - MENU_HEIGHT - 4 : rect.bottom + 4,
            right: document.documentElement.clientWidth - rect.right
        };
        openMenu = key;
    };

    const runOnSelected = async (action) => {
        const works = visible.filter(work => selected.includes(keyOf(work)));

        busy = true;
        for (const work of works) {
            await action(work.employee_id);
        }
        busy = false;
        selected = [];
    };

    const closeMenus = (event) => {
        if (!event.target.closest('.row-menu')) {
            openMenu = null;
        }
    };
</script>

<svelte:window on:click={closeMenus} on:scroll={() => openMenu = null} on:resize={() => openMenu = null}/>

<section class="view">
    <div class="filter-bar">
        <label class="filter-bar__label" for="worker-period">Miesiąc</label>
        <div class="select">
            <select id="worker-period" bind:value={period}>
                {#each periods as p}
                    <option value={p}>{p}</option>
                {/each}
            </select>
        </div>

        <div class="filter-bar__actions">
            <BulkNotificationBtn />
            <DownloadPieceBtn />
        </div>
    </div>

    {#if selected.length > 0}
        <div class="bulk-bar">
            <span class="bulk-bar__count">Wybrano {selected.length}</span>
            <div class="bulk-bar__actions">
                <button class="button is-success" disabled={busy} on:click={() => runOnSelected(approve)}>
                    <Icon name="check"/> <span>Zaakceptuj wybrane</span>
                </button>
                <button class="button is-outline-danger" disabled={busy} on:click={() => runOnSelected(reject)}>
                    <Icon name="x"/> <span>Odrzuć wybrane</span>
                </button>
                <button class="button is-ghost" disabled={busy} on:click={() => runOnSelected(sendNotification)}>
                    <Icon name="send"/> <span>Powiadom</span>
                </button>
                <button class="button is-ghost" disabled={busy} on:click={() => selected = []}>Wyczyść</button>
            </div>
        </div>
    {/if}

    <div class="table-wrap">
        <table class="table table-modern is-fullwidth">
            <thead>
                <tr>
                    <th class="select-col">
                        <input type="checkbox" aria-label="Wybierz wszystkich"
                            checked={selected.length > 0 && selected.length === selectable(visible).length}
                            on:change={(e) => selected = e.target.checked ? selectable(visible).map(keyOf) : []}>
                    </th>
                    <th>Pracownik</th>
                    <th>Status</th>
                    <th>Przesłane dzieła</th>
                    <th>Powiadomienie</th>
                    <th class="has-text-right">Akcje</th>
                </tr>
            </thead>
            <tbody>
                {#if visible.length === 0}
                    <tr>
                        <td colspan="6">
                            <div class="empty-state">
                                <img src="/logo.png" alt="" width="48" height="48">
                                <p>Brak zgłoszeń w tym okresie.</p>
                            </div>
                        </td>
                    </tr>
                {/if}
                {#each visible as work}
                    <tr>
                        <td class="select-col">
                            <input type="checkbox"
                                aria-label="Wybierz {work.employee_id.employee_email}"
                                disabled={work.status === 'NOSELECTED'}
                                checked={selected.includes(keyOf(work))}
                                on:change={() => selected = toggle(selected, keyOf(work))}>
                        </td>
                        <td>
                            <div class="employee-span">{work.employee_id.employee_email}</div>
                            <div class="cell-sub">{work.employee_id.month}.{work.employee_id.year}</div>
                        </td>
                        <td>
                            <span class={"status-chip " + statusClass(work.status)}>
                                {statusFilter(work.status)}
                            </span>
                        </td>
                        <td>
                            {#if work.pieces.length === 0}
                                <span class="cell-sub">Brak dzieł</span>
                            {:else}
                                <button class="link-btn" aria-expanded={expanded.includes(keyOf(work))}
                                    on:click={() => expanded = toggle(expanded, keyOf(work))}>
                                    <Icon name={expanded.includes(keyOf(work)) ? 'chevronDown' : 'chevronRight'}/>
                                    {work.pieces.length} {work.pieces.length === 1 ? 'dzieło' : 'dzieła'}
                                </button>
                            {/if}
                        </td>
                        <td>
                            {#if work.notification}
                                <div>Wysłano</div>
                                <div class="cell-sub">
                                    {new Date(work.notification.sent_at).toLocaleString('pl-PL')}
                                </div>
                            {:else}
                                <span class="cell-sub">Brak wysyłki dla tego okresu</span>
                            {/if}
                        </td>
                        <td>
                            <div class="row-actions">
                                <StatusBtn status={work.status} employee={work.employee_id}/>
                                <div class="dropdown row-menu" class:is-active={openMenu === keyOf(work)}>
                                    <button class="button is-ghost row-menu__trigger"
                                        aria-haspopup="true"
                                        aria-expanded={openMenu === keyOf(work)}
                                        aria-label="Więcej akcji dla {work.employee_id.employee_email}"
                                        on:click={(e) => openRowMenu(e, keyOf(work))}>
                                        <Icon name="more"/>
                                    </button>
                                    <div class="dropdown-menu row-menu__list" role="menu"
                                        style="top: {menuPos.top}px; right: {menuPos.right}px;">
                                        <div class="dropdown-content">
                                            <div class="dropdown-item"><NotificationBtn employee={work.employee_id}/></div>
                                            <div class="dropdown-item"><DownloadCardBtn employee={work.employee_id}/></div>
                                            <div class="dropdown-item"><EmployeeUrl url={work.work_url} /></div>
                                            <div class="dropdown-item"><ReloadPieces employee={work.employee_id}/></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </td>
                    </tr>

                    {#if expanded.includes(keyOf(work))}
                        <tr class="pieces-row">
                            <td></td>
                            <td colspan="5">
                                <ul class="pieces-tree">
                                    {#each work.pieces as piece}
                                        <li>
                                            <button class="link-btn"
                                                aria-expanded={openPieces.includes(keyOf(work) + piece.name)}
                                                on:click={() => openPieces = toggle(openPieces, keyOf(work) + piece.name)}>
                                                <Icon name={openPieces.includes(keyOf(work) + piece.name) ? 'chevronDown' : 'chevronRight'}/>
                                                {piece.name}
                                            </button>
                                            {#if openPieces.includes(keyOf(work) + piece.name)}
                                                <ul class="pieces-tree__tasks">
                                                    {#each piece.tasks as task}
                                                        <li><b>{task.project_key}:</b> {task.name}</li>
                                                    {/each}
                                                </ul>
                                            {/if}
                                        </li>
                                    {/each}
                                </ul>
                            </td>
                        </tr>
                    {/if}
                {/each}
            </tbody>
        </table>
    </div>
</section>
